import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  listRepositories,
  listContents,
  getFileContent,
  type GitHubRepo,
  type GitHubFile,
} from "@/services/githubService";
import {
  Search,
  FolderOpen,
  FileCode,
  FileText,
  ArrowLeft,
  Github,
  Lock,
  Globe,
  ChevronRight,
  Loader2,
  Check,
  File,
} from "lucide-react";

interface GitHubFilePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (file: {
    name: string;
    path: string;
    repo: string;
    owner: string;
    content: string;
    html_url: string;
  }) => void;
}

export function GitHubFilePicker({
  open,
  onOpenChange,
  onSelect,
}: GitHubFilePickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<GitHubFile | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const { data: repos = [], isLoading: isLoadingRepos, error: reposError } = useQuery({
    queryKey: ["github-repos"],
    queryFn: async () => {
      return await listRepositories();
    },
    enabled: open,
  });

  const { data: contents = [], isLoading: isLoadingContents } = useQuery({
    queryKey: ["github-contents", selectedRepo?.full_name, currentPath.join("/")],
    queryFn: async () => {
      if (!selectedRepo) return [];
      return await listContents(
        selectedRepo.owner.login,
        selectedRepo.name,
        currentPath.join("/")
      );
    },
    enabled: open && !!selectedRepo,
  });

  const filteredRepos = repos.filter((repo) =>
    repo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    repo.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedContents = [...contents].sort((a, b) => {
    if (a.type === "dir" && b.type === "file") return -1;
    if (a.type === "file" && b.type === "dir") return 1;
    return a.name.localeCompare(b.name);
  });

  const handleRepoSelect = (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setCurrentPath([]);
    setSelectedFile(null);
    setSearchQuery("");
  };

  const handleFolderClick = (folder: GitHubFile) => {
    setCurrentPath([...currentPath, folder.name]);
    setSelectedFile(null);
  };

  const handleFileClick = (file: GitHubFile) => {
    setSelectedFile(file);
    setImportError(null);
  };

  const handleBack = () => {
    if (currentPath.length > 0) {
      setCurrentPath(currentPath.slice(0, -1));
      setSelectedFile(null);
    } else {
      setSelectedRepo(null);
      setSelectedFile(null);
    }
  };

  const [importError, setImportError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!selectedFile || !selectedRepo) return;

    setIsLoadingContent(true);
    setImportError(null);
    try {
      const content = await getFileContent(
        selectedRepo.owner.login,
        selectedRepo.name,
        selectedFile.path
      );
      
      onSelect({
        name: content.name,
        path: content.path,
        repo: selectedRepo.name,
        owner: selectedRepo.owner.login,
        content: content.content,
        html_url: `https://github.com/${selectedRepo.owner.login}/${selectedRepo.name}/blob/${selectedRepo.default_branch}/${selectedFile.path}`,
      });
      
      onOpenChange(false);
      setSelectedRepo(null);
      setCurrentPath([]);
      setSelectedFile(null);
      setSearchQuery("");
    } catch (error: any) {
      console.error("Error fetching file content:", error);
      setImportError(error.message || "Failed to fetch file content. Please try again.");
    } finally {
      setIsLoadingContent(false);
    }
  };

  const getFileIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase();
    if (["ts", "tsx", "js", "jsx", "py", "go", "rs", "java", "cpp", "c", "h"].includes(ext || "")) {
      return <FileCode className="h-4 w-4 text-blue-500" />;
    }
    if (["md", "txt", "json", "yaml", "yml", "toml", "xml"].includes(ext || "")) {
      return <FileText className="h-4 w-4 text-gray-500" />;
    }
    return <File className="h-4 w-4 text-gray-400" />;
  };

  const renderBreadcrumb = () => {
    if (!selectedRepo) return null;
    
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3 flex-wrap">
        <span 
          className="hover:text-foreground cursor-pointer"
          onClick={() => {
            setCurrentPath([]);
            setSelectedFile(null);
          }}
        >
          {selectedRepo.name}
        </span>
        {currentPath.map((folder, index) => (
          <span key={index} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3" />
            <span 
              className="hover:text-foreground cursor-pointer"
              onClick={() => {
                setCurrentPath(currentPath.slice(0, index + 1));
                setSelectedFile(null);
              }}
            >
              {folder}
            </span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            Import from GitHub
          </DialogTitle>
          <DialogDescription>
            Browse your repositories and select a file to import as a reference source
          </DialogDescription>
        </DialogHeader>

        {!selectedRepo ? (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="flex-1 h-[400px]">
              {isLoadingRepos ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : reposError ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Github className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">Failed to load repositories</p>
                  <p className="text-sm">Please make sure GitHub is connected</p>
                </div>
              ) : filteredRepos.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Github className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No repositories found</p>
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {filteredRepos.map((repo) => (
                    <div
                      key={repo.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleRepoSelect(repo)}
                    >
                      <img
                        src={repo.owner.avatar_url}
                        alt={repo.owner.login}
                        className="w-8 h-8 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">
                            {repo.full_name}
                          </span>
                          {repo.private ? (
                            <Lock className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Globe className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        {repo.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {repo.description}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Separator orientation="vertical" className="h-6" />
              {renderBreadcrumb()}
            </div>

            <ScrollArea className="flex-1 h-[400px]">
              {isLoadingContents ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : sortedContents.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">This folder is empty</p>
                </div>
              ) : (
                <div className="space-y-1 pr-4">
                  {sortedContents.map((item) => (
                    <div
                      key={item.sha}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedFile?.sha === item.sha
                          ? "bg-primary/10 border border-primary/30"
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() =>
                        item.type === "dir"
                          ? handleFolderClick(item)
                          : handleFileClick(item)
                      }
                    >
                      {item.type === "dir" ? (
                        <FolderOpen className="h-4 w-4 text-amber-500" />
                      ) : (
                        getFileIcon(item.name)
                      )}
                      <span className="flex-1 text-sm truncate">{item.name}</span>
                      {item.type === "file" && item.size && (
                        <span className="text-xs text-muted-foreground">
                          {(item.size / 1024).toFixed(1)} KB
                        </span>
                      )}
                      {selectedFile?.sha === item.sha && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                      {item.type === "dir" && (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        )}

        <Separator />

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground flex-1">
            {importError ? (
              <span className="text-destructive">{importError}</span>
            ) : selectedFile ? (
              <span className="flex items-center gap-2">
                <Check className="h-3 w-3 text-primary" />
                Selected: {selectedFile.name}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!selectedFile || isLoadingContent}
            >
              {isLoadingContent ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                "Import File"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
